import axios from 'axios';
import {
	ADD_TIP_SSID_API,
	ENDPOINT_URL,
	GET_AP_GROUP_SSID_API,
	GET_SSID_LIST_API,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';
import {resolveByFields} from '../../core/fieldSearch';

/**
 * Return an SSID row identifier.
 * @param {object} item Canvas SSID row
 * @return {*|null} identifier
 */
function ssidId(item) {
	return item.ap_ssid_id ?? item.id ?? item.ssid_id ?? item.wlan_id ?? null;
}

/**
 * Return whether an SSID row belongs to a Site.
 * @param {object} item Canvas SSID row
 * @return {boolean} true when the row has a Site owner
 */
function isSiteOwned(item) {
	return item.site_id != null && String(item.site_id).trim() !== '';
}

/**
 * Normalize a Canvas SSID row.
 * @param {object} item Canvas SSID row
 * @param {string|null} siteId owning Site identifier
 * @return {object|null} agent candidate
 */
function normalizeSsid(item, siteId = null) {
	const id = ssidId(item);
	const name = item.ssid_name ?? item.name;
	if (id == null || !name) return null;
	const safeId = String(id).trim();
	const safeName = String(name).trim();
	if (!safeId || !safeName) return null;
	return {
		entity_type: 'ssid',
		entity_id: safeId,
		name: safeName,
		site_id: item.site_id == null ? siteId : String(item.site_id),
		site_name: optionalText(item.site_name ?? item.nested_site_name),
		security_type: optionalText(
			item.auth_type_label ?? item.auth_type ??
			item.security_type_label ?? item.security_type,
		),
		status: optionalText(item.status_label ?? (
			item.ssid_barring == null
				? null
				: Number(item.ssid_barring) === 0 ? 'Enabled' : 'Disabled'
		)),
	};
}

/**
 * Resolve SSID candidates from the authenticated Canvas API.
 * @param {object} params resolver parameters
 * @param {object} pageContext registered Canvas context
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSsids(params, pageContext = {}) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const limit = Math.min(
		Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
		LOOKUP_LIMIT,
	);
	const siteId = params.site_id || (params.current_site ? pageContext.site_id : null);
	if (['site', 'ap'].includes(params.context) && !siteId) {
		throw new Error('site_context_required');
	}
	const isTipSite = params.site_mode === 'tip' || (
		params.current_site && Number(pageContext.is_tip_enabled) === 1
	);
	const isScoped = ['site', 'ap'].includes(params.context);
	const base = isScoped
		? isTipSite
			? `${ENDPOINT_URL}${ADD_TIP_SSID_API}/?tip_id=${encodeURIComponent(siteId)}`
			: `${ENDPOINT_URL}${GET_AP_GROUP_SSID_API}/${encodeURIComponent(siteId)}/`
		: `${ENDPOINT_URL}${GET_SSID_LIST_API}`;
	const separator = base.includes('?') ? '&' : '?';
	const url = `${base}${separator}limit=${limit}&offset=0&search=` +
		encodeURIComponent(query);
	const {data} = await axios.get(url);
	const rows = responseRows(decodeAgentResponse(data) || {});
	const eligibleRows = params.context === 'global'
		? rows.filter((item) => !isSiteOwned(item))
		: rows;
	replaceResolvedEntities('ssid', eligibleRows, ssidId);
	const normalizeRow = (item) => normalizeSsid(
		item,
		siteId ? String(siteId) : null,
	);
	const matched = resolveByFields({
		rows: eligibleRows,
		normalizeRow,
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
	});
	// Canvas has already searched server-side. Narrowing to what a catalogued
	// field actually matched turns "one SSID is called this" into a single
	// result the agent can open directly.
	if (matched.length) return {candidates: matched};
	// Nothing matched a catalogued field. Canvas's own search is wider than
	// the SSID name - asking it for "Anusha" returns every SSID in the Anusha
	// Site - so handing those rows back as candidates said "I found this in
	// more than one SSID" about a name that matched none of them, and offered
	// the same list again each time. No match is the honest answer; the
	// not-found reply lists what the Site does have.
	return {candidates: []};
}

/**
 * Return the retained SSID row for editing.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedSsid(entityId) {
	return getResolvedEntity('ssid', entityId);
}
